import {
    CaretSortIcon,
    CheckIcon,
    DownloadIcon,
    UpdateIcon
} from '@radix-ui/react-icons';
import { useContext, useEffect, useState } from 'react';
import { CSVLink } from "react-csv";
import { AuthContext } from '../../context/AuthContext';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import { Label } from '../ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/use-toast';

export default function ExportJudgesClubsStatsDialog({ dropdown }) {

    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedClub, setSelectedClub] = useState(null);
    const [clubs, setClubs] = useState([]);
    const [judgesData, setJudgesData] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // New loading state
    const { auth } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const [comboboxOpen, setComboboxOpen] = useState(false);

    const fetchClubs = () => {
        if (!isOpen) return;
        setIsLoading(true); // Start loading
        api.get('https://skhrf-api.cmdigital.se/v1/clubs/')
            .then(response => {
                const allClubs = response.data;
                const isSuperAdmin = auth?.user?.superadmin;
                const userClubId = auth?.user?.memberOfClub;

                let filteredClubs;
                if (isSuperAdmin) {
                    filteredClubs = allClubs;
                } else {
                    filteredClubs = allClubs.filter(club => club.id === userClubId);
                }

                setClubs(filteredClubs);
            })
            .catch(error => {
                console.error('Error fetching clubs:', error);
            })
            .finally(() => {
                setIsLoading(false); // Stop loading
            });
    };

    const fetchJudgeStats = async () => {
        if (!selectedYear || !selectedClub) return;

        setIsLoading(true); // Start loading
        try {
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/judges/judge-stats-by-club?year=${selectedYear}&clubId=${selectedClub}`);
            setJudgesData(response.data);
            setCsvData(response.data.map(judge => ({
                year: judge.year,
                clubId: judge.clubId,
                judgeId: judge.judgeId,
                firstName: judge.firstName,
                lastName: judge.lastName,
                competitionsJudged: judge.competitionsJudged,
                competitionClassesJudged: judge.competitionClassesJudged,
                startsJudged: judge.startsJudged,
            })));
        } catch (error) {
            toast({ title: "Error fetching judge stats", description: error.message });
        } finally {
            setIsLoading(false); // Stop loading
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchClubs();
        }
    }, [isOpen]);

    useEffect(() => {
        if (selectedYear && selectedClub) {
            fetchJudgeStats();
        }
    }, [selectedYear, selectedClub]);

    const generateYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = 2022; year <= currentYear; year++) {
            years.push(year);
        }
        return years;
    };

    const headers = [
        { label: "År", key: "year" },
        { label: "Förenings ID", key: "clubId" },
        { label: "Domar ID", key: "judgeId" },
        { label: "Förnamn", key: "firstName" },
        { label: "Efternamn", key: "lastName" },
        { label: "Dömda tävlingar", key: "competitionsJudged" },
        { label: "Dömda klasser", key: "competitionClassesJudged" },
        { label: "Dömda starter", key: "startsJudged" },
    ];

    const selectedClubName = clubs.find(club => club.id === selectedClub)?.name || 'Välj förening';

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            {dropdown ? (
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                    <DialogTrigger className='flex items-center w-full'>
                        <DownloadIcon className='mr-3'/>
                        Exportera föreningsspecifik domarstatistik
                    </DialogTrigger>
                </DropdownMenuItem>
            ) : (
                <DialogTrigger className='flex items-center w-full' asChild>
                    <Button>
                        <DownloadIcon className='mr-3'/>
                        Exportera föreningsspecifik domarstatistik
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent className="flex flex-col gap-6" onOpenAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader className='text-left'>
                    <DialogTitle>Exportera statistik för domare</DialogTitle>
                    <DialogDescription>
                        Välj år och förening nedan för att ladda ner statistik för domare.
                    </DialogDescription>
                </DialogHeader>
                <div className='flex flex-col gap-2'>
                    <Label htmlFor='selectedClub'>Förening</Label>
                    <Popover modal={true} open={comboboxOpen} onOpenChange={setComboboxOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className={cn(
                                    "w-full justify-between",
                                    !selectedClub && "text-muted-foreground"
                                )}
                                disabled={isLoading}
                            >
                                {selectedClub
                                    ? clubs.find((club) => club.id === selectedClub)?.name
                                    : "Välj förening"}
                                <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="popover-content-width-full p-0">
                            <Command>
                                <CommandInput placeholder="Sök förening..." />
                                <CommandList>
                                    <CommandEmpty>Ingen förening hittades.</CommandEmpty>
                                    <CommandGroup>
                                        {clubs.map((club) => (
                                            <CommandItem
                                                key={club.id}
                                                onSelect={() => {
                                                    setSelectedClub(club.id)
                                                    setComboboxOpen(false)
                                                  }}
                                            >
                                                <CheckIcon
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        club.id === selectedClub ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                {club.name}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
                <div className='flex flex-col gap-2'>
                    <Label htmlFor='selectedYear'>År</Label>
                    <Select
                        value={selectedYear?.toString() || ''}
                        onValueChange={(year) => setSelectedYear(parseInt(year))}
                        disabled={isLoading}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Välj år..." />
                        </SelectTrigger>
                        <SelectContent>
                            {generateYears().map(year => (
                                <SelectItem key={year} value={year.toString()}>
                                    {year}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {selectedYear && (
                    judgesData.length > 0 ? (
                        <p className='text-sm'>{judgesData.length} domare har tillgänglig data för {selectedClubName} för året {selectedYear}.</p>
                    ) : (
                        <p className='text-sm'>Inga domare har tillgänglig data för {selectedClubName} för året {selectedYear}.</p>
                    )
                )}

                <div className='grid grid-cols-2 gap-3'>
                    <DialogClose asChild>
                        <Button variant='secondary'>Stäng</Button>
                    </DialogClose>
                    <Button disabled={judgesData.length <= 0 || isLoading}>
                        {isLoading ? (
                            <UpdateIcon className="loader-spinner" />
                        ) : (
                            <CSVLink
                                data={csvData}
                                headers={headers}
                                filename={`judges_stats_${selectedYear}_club_${selectedClub}.csv`}
                                className='flex items-center justify-center w-full'>
                                <DownloadIcon className="mr-3" />
                                Ladda ner
                            </CSVLink>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
